package org.zq.astrock_ai.ui.research.gushi.data

import org.zq.astrock_ai.storage.AdaptiveLearningStorage
import org.zq.astrock_ai.ui.research.gushi.model.Candle
import kotlin.math.abs
import kotlin.math.pow
import kotlin.math.roundToInt
import kotlin.math.sqrt

data class AdaptiveLearningResult(
    val historicalSamples: Int = 0,
    val validationSamples: Int = 0,
    val validationHitRate: Double? = null,
    val forwardSamples: Int = 0,
    val forwardHitRate: Double? = null,
    val scoreAdjustment: Int = 0,
    val weightSummary: String = "",
    val horizonSummary: String = "",
    val benchmarkName: String = "",
    val marketRegime: String = "",
    val marketCause: String = "",
    val stockImpact: String = "",
    val beta: Double? = null,
    val correlation: Double? = null,
)

/**
 * 本地自适应校准器：
 * 1. 只用某日及以前的数据生成特征，以第 5 个后续交易日收益作为标签；
 * 2. 学习各指标对当前股票的历史有效权重；
 * 3. 保存真实每日预测，到期后自动复盘。
 */
object AdaptiveStockLearner {
    private val featureNames = listOf("均线", "MACD", "RSI", "动量", "量价", "突破", "市场")
    private const val horizon = 5
    private val horizons = listOf(1, 3, 5, 10)
    private const val outcomeThresholdPercent = 0.7
    private val resultCache = mutableMapOf<EvaluationCacheKey, AdaptiveLearningResult>()

    fun evaluate(
        code: String,
        candles: List<Candle>,
        benchmarkCandles: List<Candle> = emptyList(),
        benchmarkName: String = "",
    ): AdaptiveLearningResult {
        if (candles.size < 50) return AdaptiveLearningResult()
        if (benchmarkCandles.size < 50) {
            return AdaptiveLearningResult(
                benchmarkName = benchmarkName,
                marketRegime = "基准数据不足，暂停模型校准",
                marketCause = "无法取得完整大盘数据，本轮不训练、不调整建议。",
            )
        }
        val cacheKey = EvaluationCacheKey(
            code = code,
            stockDate = candles.last().date,
            stockClose = candles.last().close,
            benchmarkDate = benchmarkCandles.last().date,
            benchmarkClose = benchmarkCandles.last().close,
        )
        resultCache[cacheKey]?.let { return it }
        val benchmarkByDate = benchmarkCandles.associateBy { it.date.take(10) }
        val marketAnalysis = analyzeMarket(candles, benchmarkCandles)
        val signalMatrix = buildSignalMatrix(candles, benchmarkCandles)

        val samples = (30..candles.lastIndex - horizons.max()).mapNotNull { index ->
            val signs = signalMatrix[index]
            val entry = candles[index].close
            val benchmarkEntry = benchmarkByDate[candles[index].date.take(10)]?.close
                ?: return@mapNotNull null
            if (entry <= 0.0) {
                null
            } else {
                val outcomes = mutableMapOf<Int, Int>()
                for (days in horizons) {
                    val futureCandle = candles[index + days]
                    val benchmarkFuture = benchmarkByDate[futureCandle.date.take(10)]?.close
                        ?: return@mapNotNull null
                    val stockReturn = (futureCandle.close - entry) / entry * 100.0
                    val benchmarkReturn = (benchmarkFuture - benchmarkEntry) / benchmarkEntry * 100.0
                    outcomes[days] = direction(
                        stockReturn - benchmarkReturn,
                        threshold = outcomeThresholdPercent,
                    )
                }
                TrainingSample(
                    candleIndex = index,
                    signs = signs,
                    outcomes = outcomes,
                )
            }
        }

        val weights = learnWeights(samples, horizon)
        val horizonStats = horizons.associateWith { days ->
            walkForwardValidate(samples, days)
        }
        val primaryValidation = horizonStats[horizon] ?: HorizonStat()

        val currentSigns = signalMatrix.last()
        val normalizedSignal = weightedSignal(currentSigns, weights) /
            weights.sum().coerceAtLeast(1.0)
        val reliability = when {
            primaryValidation.samples < 10 -> 0.0
            (primaryValidation.hitRate ?: 0.0) < 45.0 -> 0.0
            (primaryValidation.hitRate ?: 0.0) < 55.0 -> 0.5
            else -> 1.0
        }
        var adjustment = if (samples.size >= 30) {
            (normalizedSignal * 8.0 * reliability).roundToInt().coerceIn(-8, 8)
        } else {
            0
        }
        if (marketAnalysis.isWeak && adjustment > 0) {
            adjustment = if (marketAnalysis.isSeverelyWeak) 0 else adjustment.coerceAtMost(2)
        }

        val records = updateForwardRecords(code, candles, direction(normalizedSignal))
        val resolved = records.filter { it.code == code && it.outcome != null }
        val forwardRate = resolved.takeIf { it.isNotEmpty() }?.let { list ->
            list.count { direction(it.outcome ?: 0.0, threshold = 0.5) == it.signal }
                .toDouble() / list.size * 100.0
        }
        val weightSummary = weights.indices
            .sortedByDescending { abs(weights[it] - 1.0) }
            .take(3)
            .joinToString("、") { "${featureNames[it]}×${round2(weights[it])}" }
        val horizonSummary = horizons.joinToString("；") { days ->
            val stat = horizonStats[days] ?: HorizonStat()
            "${days}日 ${stat.hitRate?.roundToInt()?.let { "$it%" } ?: "--"}(${stat.samples})"
        }

        val result = AdaptiveLearningResult(
            historicalSamples = samples.count { it.outcomes[horizon] != 0 },
            validationSamples = primaryValidation.samples,
            validationHitRate = primaryValidation.hitRate,
            forwardSamples = resolved.size,
            forwardHitRate = forwardRate,
            scoreAdjustment = adjustment,
            weightSummary = weightSummary,
            horizonSummary = horizonSummary,
            benchmarkName = benchmarkName,
            marketRegime = marketRegime(benchmarkCandles),
            marketCause = marketAnalysis.cause,
            stockImpact = marketAnalysis.stockImpact,
            beta = marketAnalysis.beta,
            correlation = marketAnalysis.correlation,
        )
        resultCache[cacheKey] = result
        while (resultCache.size > 40) {
            resultCache.remove(resultCache.keys.first())
        }
        return result
    }

    private fun learnWeights(
        samples: List<TrainingSample>,
        days: Int,
    ): List<Double> = featureNames.indices.map { feature ->
        val usable = samples.filter { sample ->
            sample.signs[feature] != 0 && sample.outcomes[days] != 0
        }
        if (usable.size < 15) {
            1.0
        } else {
            var weightedHits = 0.0
            var totalWeight = 0.0
            usable.forEachIndexed { index, sample ->
                // 半衰期约 40 个样本，让近期市场结构获得更高权重。
                val age = usable.lastIndex - index
                val timeWeight = 0.5.pow(age / 40.0)
                totalWeight += timeWeight
                if (sample.signs[feature] == sample.outcomes[days]) {
                    weightedHits += timeWeight
                }
            }
            // 以 50% 为先验进行平滑，避免小样本权重极端化。
            val accuracy = (weightedHits + 5.0) / (totalWeight + 10.0)
            (0.6 + ((accuracy - 0.35) / 0.30).coerceIn(0.0, 1.0) * 0.8)
        }
    }

    /**
     * 后40%按时间顺序逐点验证。每个验证点只允许使用标签已到期的更早样本。
     */
    private fun walkForwardValidate(
        samples: List<TrainingSample>,
        days: Int,
    ): HorizonStat {
        if (samples.size < 20) return HorizonStat()
        val validationStart = (samples.size * 0.6).roundToInt().coerceIn(10, samples.lastIndex)
        var tested = 0
        var hits = 0
        samples.drop(validationStart).forEach { sample ->
            val outcome = sample.outcomes[days] ?: 0
            if (outcome == 0) return@forEach
            val maturedTraining = samples.take(validationStart).filter {
                it.candleIndex + days <= sample.candleIndex
            } + samples.drop(validationStart).takeWhile {
                it.candleIndex + days <= sample.candleIndex
            }
            if (maturedTraining.size < 10) return@forEach
            val weights = learnWeights(maturedTraining.distinctBy { it.candleIndex }, days)
            val prediction = direction(weightedSignal(sample.signs, weights))
            if (prediction == 0) return@forEach
            tested++
            if (prediction == outcome) hits++
        }
        return HorizonStat(
            samples = tested,
            hitRate = tested.takeIf { it > 0 }?.let { hits.toDouble() / it * 100.0 },
        )
    }

    /** 一次性预计算全部日期特征，避免滚动验证中重复计算 EMA/MACD。 */
    private fun buildSignalMatrix(
        candles: List<Candle>,
        benchmarkCandles: List<Candle>,
    ): List<IntArray> {
        val closes = candles.map { it.close }
        val closePrefix = DoubleArray(candles.size + 1)
        val volumePrefix = DoubleArray(candles.size + 1)
        candles.indices.forEach { index ->
            closePrefix[index + 1] = closePrefix[index] + candles[index].close
            volumePrefix[index + 1] = volumePrefix[index] + candles[index].volume
        }
        fun average(prefix: DoubleArray, endInclusive: Int, count: Int): Double {
            val start = maxOf(0, endInclusive - count + 1)
            val actualCount = endInclusive - start + 1
            return if (actualCount > 0) {
                (prefix[endInclusive + 1] - prefix[start]) / actualCount
            } else {
                0.0
            }
        }

        val ema12 = emaSeriesFast(closes, 12)
        val ema26 = emaSeriesFast(closes, 26)
        val dif = closes.indices.map { ema12[it] - ema26[it] }
        val dea = emaSeriesFast(dif, 9)

        val benchmarkCloses = benchmarkCandles.map { it.close }
        val benchmarkPrefix = DoubleArray(benchmarkCandles.size + 1)
        benchmarkCandles.indices.forEach { index ->
            benchmarkPrefix[index + 1] = benchmarkPrefix[index] + benchmarkCandles[index].close
        }
        val marketSignalsByDate = benchmarkCandles.indices.associate { index ->
            val signal = if (index >= 19) {
                direction(
                    average(benchmarkPrefix, index, 5) - average(benchmarkPrefix, index, 20),
                )
            } else {
                0
            }
            benchmarkCandles[index].date.take(10) to signal
        }

        return candles.indices.map { index ->
            val current = candles[index]
            val maSignal = direction(
                average(closePrefix, index, 5) - average(closePrefix, index, 20),
            )
            val macdSignal = direction(dif[index] - dea[index])

            var gain = 0.0
            var loss = 0.0
            var changes = 0
            for (i in maxOf(1, index - 13)..index) {
                val change = closes[i] - closes[i - 1]
                if (change > 0.0) gain += change else loss -= change
                changes++
            }
            val rsiValue = when {
                changes == 0 -> 50.0
                loss == 0.0 -> if (gain == 0.0) 50.0 else 100.0
                else -> 100.0 - 100.0 / (1.0 + gain / loss)
            }
            val rsiSignal = when {
                rsiValue <= 30.0 -> 1
                rsiValue >= 70.0 -> -1
                else -> 0
            }
            val momentumSignal = direction(
                current.close - closes[maxOf(0, index - 10)],
            )

            val recentVolume = average(volumePrefix, index, 5)
            val priorEnd = index - 5
            val priorVolume = if (priorEnd >= 0) average(volumePrefix, priorEnd, 20) else recentVolume
            val volumeRatio = if (priorVolume > 0.0) recentVolume / priorVolume else 1.0
            val previousClose = closes[maxOf(0, index - 1)]
            val volumeSignal = if (volumeRatio >= 1.1) {
                direction(current.close - previousClose)
            } else {
                0
            }

            val priorWindow = candles.subList(maxOf(0, index - 20), index)
            val breakoutSignal = when {
                priorWindow.isEmpty() -> 0
                current.close > priorWindow.maxOf { it.high } -> 1
                current.close < priorWindow.minOf { it.low } -> -1
                else -> 0
            }
            val marketSignal = marketSignalsByDate[current.date.take(10)] ?: 0
            intArrayOf(
                maSignal,
                macdSignal,
                rsiSignal,
                momentumSignal,
                volumeSignal,
                breakoutSignal,
                marketSignal,
            )
        }
    }

    private fun emaSeriesFast(values: List<Double>, period: Int): DoubleArray {
        if (values.isEmpty()) return DoubleArray(0)
        val result = DoubleArray(values.size)
        val factor = 2.0 / (period + 1.0)
        result[0] = values.first()
        for (index in 1..values.lastIndex) {
            result[index] = result[index - 1] + (values[index] - result[index - 1]) * factor
        }
        return result
    }

    private fun marketRegime(benchmarkCandles: List<Candle>): String {
        if (benchmarkCandles.size < 20) return "基准数据不足"
        val closes = benchmarkCandles.map { it.close }
        val ma5 = closes.takeLast(5).average()
        val ma20 = closes.takeLast(20).average()
        val return20 = (closes.last() - closes[closes.lastIndex - 19]) /
            closes[closes.lastIndex - 19] * 100.0
        return when {
            ma5 > ma20 && return20 >= 3.0 -> "偏强趋势"
            ma5 < ma20 && return20 <= -3.0 -> "偏弱趋势"
            ma5 > ma20 -> "震荡偏强"
            ma5 < ma20 -> "震荡偏弱"
            else -> "横盘震荡"
        }
    }

    private fun analyzeMarket(
        stockCandles: List<Candle>,
        benchmarkCandles: List<Candle>,
    ): MarketAnalysis {
        val benchmarkReturns = benchmarkCandles.zipWithNext { previous, current ->
            current.date.take(10) to
                if (previous.close > 0.0) (current.close - previous.close) / previous.close * 100.0 else 0.0
        }.toMap()
        val stockReturns = stockCandles.zipWithNext { previous, current ->
            current.date.take(10) to
                if (previous.close > 0.0) (current.close - previous.close) / previous.close * 100.0 else 0.0
        }.toMap()
        val aligned = stockReturns.keys.intersect(benchmarkReturns.keys)
            .sorted()
            .takeLast(60)
            .map { date -> stockReturns.getValue(date) to benchmarkReturns.getValue(date) }

        val stockValues = aligned.map { it.first }
        val marketValues = aligned.map { it.second }
        val stockMean = stockValues.takeIf { it.isNotEmpty() }?.average() ?: 0.0
        val marketMean = marketValues.takeIf { it.isNotEmpty() }?.average() ?: 0.0
        val covariance = aligned.takeIf { it.isNotEmpty() }?.sumOf { (stock, market) ->
            (stock - stockMean) * (market - marketMean)
        }?.div(aligned.size) ?: 0.0
        val stockVariance = stockValues.takeIf { it.isNotEmpty() }?.sumOf {
            (it - stockMean) * (it - stockMean)
        }?.div(stockValues.size) ?: 0.0
        val marketVariance = marketValues.takeIf { it.isNotEmpty() }?.sumOf {
            (it - marketMean) * (it - marketMean)
        }?.div(marketValues.size) ?: 0.0
        val beta = marketVariance.takeIf { it > 1e-9 }?.let { covariance / it }
        val correlation = if (stockVariance > 1e-9 && marketVariance > 1e-9) {
            covariance / sqrt(stockVariance * marketVariance)
        } else {
            null
        }

        val benchmarkCloses = benchmarkCandles.map { it.close }
        val latestReturn = benchmarkReturns[benchmarkCandles.last().date.take(10)] ?: 0.0
        val return5 = periodReturn(benchmarkCloses, 5)
        val return20 = periodReturn(benchmarkCloses, 20)
        val stockReturn20 = periodReturn(stockCandles.map { it.close }, 20)
        val relativeStrength20 = stockReturn20 - return20
        val ma20 = benchmarkCloses.takeLast(20).average()
        val drawdown20 = (benchmarkCloses.last() /
            benchmarkCloses.takeLast(20).maxOrNull().orEmptyPrice() - 1.0) * 100.0
        val recentVolume = benchmarkCandles.takeLast(5).map { it.volume }.average()
        val priorVolumes = benchmarkCandles.dropLast(5).takeLast(20).map { it.volume }
        val volumeRatio = priorVolumes.takeIf { it.isNotEmpty() && it.average() > 0.0 }
            ?.let { recentVolume / it.average() }
            ?: 1.0
        val consecutiveDown = benchmarkCandles.zipWithNext { previous, current ->
            current.close < previous.close
        }.asReversed().takeWhile { it }.size

        val causes = mutableListOf<String>()
        when {
            latestReturn <= -0.5 && volumeRatio >= 1.15 ->
                causes += "指数放量下跌，风险偏好下降"
            latestReturn < 0.0 && volumeRatio <= 0.85 ->
                causes += "指数缩量下跌，增量资金不足"
            latestReturn < 0.0 ->
                causes += "指数当日走弱"
        }
        if (benchmarkCloses.last() < ma20) causes += "指数位于20日均线下方"
        if (return5 <= -2.0) causes += "近5日累计回落 ${round2(return5)}%"
        if (drawdown20 <= -3.0) causes += "距20日高点回撤 ${round2(drawdown20)}%"
        if (consecutiveDown >= 3) causes += "已连续下跌 $consecutiveDown 个交易日"
        val cause = if (causes.isEmpty()) {
            "当前大盘未出现明确的量价下跌组合，主要按震荡环境处理"
        } else {
            causes.joinToString("；")
        }

        val impactLevel = when {
            correlation == null || beta == null -> "影响样本不足"
            correlation >= 0.65 && beta >= 1.2 -> "高敏感"
            correlation >= 0.40 && beta >= 0.8 -> "中等偏高"
            correlation >= 0.20 -> "中等"
            correlation <= -0.20 -> "逆向相关"
            else -> "较低"
        }
        val stockImpact = buildString {
            append("大盘影响 $impactLevel")
            if (beta != null) append("，Beta ${round2(beta)}")
            if (correlation != null) append("，相关系数 ${round2(correlation)}")
            append("；近20日相对基准 ${if (relativeStrength20 > 0) "+" else ""}${round2(relativeStrength20)}%。")
            when {
                correlation != null && correlation >= 0.4 && latestReturn < 0.0 ->
                    append(" 大盘下跌对该股有较明显拖累。")
                correlation != null && correlation <= -0.2 && latestReturn < 0.0 ->
                    append(" 该股历史上具有一定逆势特征，但仍需资金确认。")
                else -> append(" 大盘传导影响有限，优先观察个股资金与板块。")
            }
        }
        return MarketAnalysis(
            cause = cause,
            stockImpact = stockImpact,
            beta = beta,
            correlation = correlation,
            isWeak = return5 < -1.0 || benchmarkCloses.last() < ma20,
            isSeverelyWeak = return5 <= -4.0 || drawdown20 <= -6.0,
        )
    }

    private fun periodReturn(closes: List<Double>, days: Int): Double {
        if (closes.size <= days) return 0.0
        val start = closes[closes.lastIndex - days]
        return if (start > 0.0) (closes.last() - start) / start * 100.0 else 0.0
    }

    private fun Double?.orEmptyPrice(): Double = this?.takeIf { it > 0.0 } ?: 1.0

    private fun updateForwardRecords(
        code: String,
        candles: List<Candle>,
        currentSignal: Int,
    ): List<PredictionRecord> {
        var records = decodeRecords(AdaptiveLearningStorage.load())
        records = records.map { record ->
            if (record.code != code || record.outcome != null) return@map record
            val index = candles.indexOfFirst { it.date.take(10) == record.date }
            if (index < 0 || index + horizon > candles.lastIndex) return@map record
            val futurePrice = candles[index + horizon].close
            record.copy(outcome = (futurePrice - record.price) / record.price * 100.0)
        }

        val latest = candles.last()
        val date = latest.date.take(10)
        if (date.isNotBlank() && records.none { it.code == code && it.date == date }) {
            records = records + PredictionRecord(
                code = code,
                date = date,
                price = latest.close,
                signal = currentSignal,
                outcome = null,
            )
        }
        records = records.takeLast(120)
        AdaptiveLearningStorage.save(encodeRecords(records))
        return records
    }

    private fun weightedSignal(signs: IntArray, weights: List<Double>): Double =
        signs.indices.sumOf { signs[it] * weights[it] }

    private fun direction(value: Double, threshold: Double = 0.0): Int = when {
        value > threshold -> 1
        value < -threshold -> -1
        else -> 0
    }

    private fun encodeRecords(records: List<PredictionRecord>): String =
        records.joinToString("\n") { record ->
            listOf(
                record.code,
                record.date,
                record.price.toString(),
                record.signal.toString(),
                record.outcome?.toString().orEmpty(),
            ).joinToString("\t")
        }

    private fun decodeRecords(raw: String?): List<PredictionRecord> =
        raw.orEmpty().lineSequence().mapNotNull { line ->
            val parts = line.split('\t')
            if (parts.size < 5) return@mapNotNull null
            PredictionRecord(
                code = parts[0],
                date = parts[1],
                price = parts[2].toDoubleOrNull() ?: return@mapNotNull null,
                signal = parts[3].toIntOrNull() ?: return@mapNotNull null,
                outcome = parts[4].toDoubleOrNull(),
            )
        }.toList()

    private fun round2(value: Double): Double =
        (value * 100.0).roundToInt() / 100.0

    private data class TrainingSample(
        val candleIndex: Int,
        val signs: IntArray,
        val outcomes: Map<Int, Int>,
    )

    private data class HorizonStat(
        val samples: Int = 0,
        val hitRate: Double? = null,
    )

    private data class MarketAnalysis(
        val cause: String,
        val stockImpact: String,
        val beta: Double?,
        val correlation: Double?,
        val isWeak: Boolean,
        val isSeverelyWeak: Boolean,
    )

    private data class EvaluationCacheKey(
        val code: String,
        val stockDate: String,
        val stockClose: Double,
        val benchmarkDate: String,
        val benchmarkClose: Double,
    )

    private data class PredictionRecord(
        val code: String,
        val date: String,
        val price: Double,
        val signal: Int,
        val outcome: Double?,
    )
}

fun StockTrendMetrics.withAdaptiveLearning(result: AdaptiveLearningResult): StockTrendMetrics {
    if (result.historicalSamples == 0) {
        return if (result.marketCause.isBlank()) {
            this
        } else {
            copy(
                analysis = "$analysis 大盘分析：${result.marketCause}",
                riskHint = "$riskHint ${result.marketRegime}，自适应训练已暂停。",
            )
        }
    }
    val baseScore = upsideProb.filter(Char::isDigit).toIntOrNull() ?: 50
    val calibrated = (baseScore + result.scoreAdjustment).coerceIn(10, 90)
    val validationRate = result.validationHitRate?.let { "${it.roundToInt()}%" } ?: "--"
    val forwardText = if (result.forwardSamples > 0) {
        "真实复盘 ${result.forwardSamples} 次，方向一致率 ${result.forwardHitRate?.roundToInt() ?: 0}%"
    } else {
        "真实复盘样本积累中（预测将在5个交易日后验收）"
    }
    val benchmarkText = result.benchmarkName.takeIf { it.isNotBlank() }
        ?.let { "基准 $it，市场状态 ${result.marketRegime}，验证目标为跑赢基准；" }
        .orEmpty()
    return copy(
        upsideProb = "$calibrated%",
        weakenProb = "${100 - calibrated}%",
        analysis = "$analysis 大盘分析：${result.marketCause}。对个股影响：${result.stockImpact} 自适应学习：$benchmarkText${result.historicalSamples} 个已标注样本；扩展窗口样本外验证 ${result.validationSamples} 次，5日超额收益方向一致率 $validationRate；多周期验证 ${result.horizonSummary}；$forwardText；当前校准 ${if (result.scoreAdjustment > 0) "+" else ""}${result.scoreAdjustment} 分。",
        positionDesc = "$positionDesc 个股指标权重：${result.weightSummary}。",
        buyPlan = "$buyPlan\n大盘确认：市场处于${result.marketRegime}；大盘走弱时，需等待指数止跌、个股相对强度转正后再执行。",
        riskHint = "$riskHint 自适应结果仅用于校准；样本外验证少于10次或真实复盘少于20次时仍属于观察期，验证低于45%时不参与评分。${if (result.marketRegime.contains("偏弱")) " 当前大盘偏弱，模型已限制正向加分。" else ""}",
    )
}
